<script setup lang="ts">
import {computed, ref, watch} from "vue";
import {Cake, Gift, Send, Smartphone, TriangleAlert, Plus} from "@lucide/vue";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from "@/core/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "@/core/components/ui/select";
import {Button} from "@/core/components/ui/button";
import GiftStatusBadge from "@/birthday-gift/components/GiftStatusBadge.vue";
import {AgendaBirthday} from "@/core/interfaces/AgendaBirthday";
import {BirthdayGift, GiftSendHour} from "@/birthday-gift/interfaces";
import {BirthdaySendRecipient, birthdayDateKey} from "@/agenda/lib/birthdays";
import {parseAgendaDate} from "@/agenda/lib/agendaTime";
import formatHelper from "@/core/helpers/formatHelper";

const {
    day,
    birthdays,
    gifts = [],
    sendHour,
    sendingIds = [],
    sendingAll = false,
    failed = false
} = defineProps<{
    /** The day the panel shows, today or any other the agenda navigated to. */
    day: Date;
    /** The birthdays of that day, targeted by a gift or not. */
    birthdays: AgendaBirthday[];
    /** Active gifts, offered for the patients no gift targets. */
    gifts?: BirthdayGift[];
    /** Hour the automatic dispatch runs at, for the scheduled badge. */
    sendHour?: GiftSendHour;
    /** Patients whose row is awaiting the server. */
    sendingIds?: number[];
    sendingAll?: boolean;
    /** The list could not be loaded: an empty panel must not claim nobody. */
    failed?: boolean;
}>();

const open = defineModel<boolean>('open', {required: true});

const emits = defineEmits<{
    (event: 'send', payload: {customerId: number; giftId?: number; phone?: string}): void;
    (event: 'sendAll', recipients: BirthdaySendRecipient[]): void;
    (event: 'createGift'): void;
}>();

/**
 * Gift picked by the pharmacist for a patient no gift targets, keyed by
 * customer. Reset when the panel reopens: the choice belongs to the sitting.
 */
const pickedGifts = ref<Record<number, number>>({});

/**
 * Mobile typed at the counter for a patient whose file holds no number a
 * message can reach, keyed by customer. Kept as typed, normalised on send.
 */
const typedPhones = ref<Record<number, string>>({});

watch(open, (isOpen) => {
    if (isOpen) {
        pickedGifts.value = {};
        typedPhones.value = {};
    }
});

/**
 * The typed number in the form the API expects, or null while it is not yet
 * a French mobile — the only kind RCS and its SMS failover can reach.
 */
const typedPhoneOf = (birthday: AgendaBirthday): string | null => {
    const typed = typedPhones.value[birthday.customer_id];

    return typed && formatHelper.phoneIsValid(typed) ? formatHelper.apiPhone(typed) : null;
};

/** The number a send would leave for, whatever its origin. */
const phoneToUse = (birthday: AgendaBirthday): string | null =>
    birthday.contactable ? null : typedPhoneOf(birthday);

/**
 * Reformat the number as it is typed, and write the result back into the
 * field itself.
 *
 * The write-back is the point, not a precaution: the formatter drops anything
 * past the tenth digit, so an eleventh keystroke normalises to the string
 * already held. Vue would then see no change and patch nothing, leaving the
 * field showing a number the send would not use — and this field decides
 * where a patient's message goes.
 */
const onPhoneInput = (birthday: AgendaBirthday, event: Event) => {
    const field = event.target as HTMLInputElement;
    const formatted = formatHelper.readablePhone(field.value);

    typedPhones.value[birthday.customer_id] = formatted;

    if (field.value !== formatted) {
        field.value = formatted;
    }
};

const initialsOf = (birthday: AgendaBirthday): string =>
    `${birthday.first_name.charAt(0)}${birthday.last_name.charAt(0)}`.toUpperCase();

/**
 * Sending is only possible on the day itself: the voucher the gift pipeline
 * creates is stamped with the current date, so a birthday still to come has
 * nothing to send yet.
 */
const isToday = computed<boolean>(() => birthdayDateKey(day) === birthdayDateKey(new Date()));

const dayLabel = computed<string>(() => parseAgendaDate(day).format('dddd D MMMM'));

/**
 * The message is already on its way on its own — the pharmacist needs to know
 * before sending a second one by hand.
 */
const isAutomatic = (birthday: AgendaBirthday): boolean => birthday.voucher?.status === 'scheduled';

/**
 * Why a patient cannot be sent to, or null when they can. The gift pipeline
 * owns these rules; the panel only states them.
 */
const refusalOf = (birthday: AgendaBirthday): string | null => {
    if (!isToday.value) {
        return 'L\'envoi n\'est possible que le jour de l\'anniversaire.';
    }

    if (birthday.voucher?.message_delivered) {
        return 'Ce patient a déjà reçu son cadeau cette année.';
    }

    if (!birthday.contactable && typedPhoneOf(birthday) === null) {
        return 'Saisissez un mobile pour pouvoir écrire à ce patient.';
    }

    return null;
};

const sendable = computed<AgendaBirthday[]>(() => birthdays.filter(birthday => refusalOf(birthday) === null));

/**
 * Everyone reachable is included: each carries the gift that targets them or
 * the one just picked, and a patient with neither still gets plain birthday
 * wishes rather than being left out.
 */
const recipients = computed<BirthdaySendRecipient[]>(() => sendable.value.map(birthday => ({
    customer_id: birthday.customer_id,
    ...(birthday.gift === null && pickedGifts.value[birthday.customer_id]
        ? {birthday_gift_id: pickedGifts.value[birthday.customer_id]}
        : {}),
    ...(phoneToUse(birthday) ? {phone: phoneToUse(birthday) as string} : {})
})));

/** Whether a gift travels with the message, or plain wishes alone. */
const sendsGift = (birthday: AgendaBirthday): boolean =>
    birthday.gift !== null || pickedGifts.value[birthday.customer_id] !== undefined;

/** Patients the batch will greet with plain wishes rather than a gift. */
const wishesOnlyCount = computed<number>(
    () => sendable.value.filter(birthday => !sendsGift(birthday)).length
);

/**
 * What the phone line says. The number only travels on the celebration day,
 * so its absence on any other day means « withheld », never « none » — and a
 * patient with a perfectly good mobile must not be announced as unreachable.
 */
const phoneLabel = (birthday: AgendaBirthday): string => {
    if (birthday.phone) {
        return birthday.phone;
    }

    if (!isToday.value) {
        return birthday.contactable
            ? 'Joignable par mobile'
            : 'Aucun numéro permettant l\'envoi du message';
    }

    return 'Aucun numéro permettant l\'envoi du message';
};

/**
 * Said in the footer: a count that drops must be explained, not just smaller.
 * A number typed at the counter takes its patient out of it.
 */
const unreachableCount = computed<number>(() => birthdays.filter(
    birthday => !birthday.contactable && typedPhoneOf(birthday) === null
).length);

const isSending = (birthday: AgendaBirthday): boolean =>
    sendingAll || sendingIds.includes(birthday.customer_id);

const send = (birthday: AgendaBirthday) => emits('send', {
    customerId: birthday.customer_id,
    // Only when the pharmacist picked it: a matched gift is the server's own
    // choice and must not be pinned as an override.
    giftId: birthday.gift ? undefined : pickedGifts.value[birthday.customer_id],
    phone: phoneToUse(birthday) ?? undefined
});
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="flex max-h-[90vh] max-w-xl flex-col">
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2">
          <Cake class="size-5 text-primary" />
          {{ isToday ? 'Anniversaires du jour' : 'Anniversaires' }}
        </DialogTitle>
        <DialogDescription class="first-letter:uppercase">
          <template v-if="isToday">
            {{ birthdays.length }}
            {{ birthdays.length > 1 ? "patients fêtent leur" : "patient fête son" }}
            anniversaire aujourd'hui
          </template>
          <template v-else>
            {{ dayLabel }} — l'envoi se fera le jour même
          </template>
        </DialogDescription>
      </DialogHeader>

      <div
        v-if="failed"
        class="flex items-center gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2"
      >
        <TriangleAlert class="size-4 shrink-0 text-amber-700" />
        <span class="text-[11.5px] font-medium text-amber-900">
          Les anniversaires n'ont pas pu être chargés : cette liste peut être incomplète.
        </span>
      </div>

      <div
        v-else-if="!birthdays.length"
        class="rounded-lg border border-dashed border-gray-200 px-3 py-6 text-center text-xs text-gray-400"
      >
        Aucun anniversaire aujourd'hui.
      </div>

      <!-- A short window must not swallow the send button: the rows scroll. -->
      <div v-else class="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-1">
        <div
          v-for="birthday in birthdays"
          :key="birthday.customer_id"
          class="flex flex-col gap-2 rounded-lg border border-gray-100 px-3 py-2.5"
        >
          <div class="flex items-center gap-3">
            <div
              class="flex size-9 flex-none items-center justify-center rounded-full bg-primary-lightest text-[11px] font-semibold text-primary"
            >
              {{ initialsOf(birthday) }}
            </div>

            <div class="min-w-0 flex-1">
              <div class="truncate text-xs font-semibold text-gray-800">
                {{ birthday.first_name }} {{ birthday.last_name }} · {{ birthday.age }} ans
              </div>
              <!-- One line each, always: the counter reads down a column
                   rather than hunting through a wrapped sentence. -->
              <div class="mt-0.5 flex flex-col gap-0.5 text-[10.5px]">
                <span
                  class="flex items-center gap-1"
                  :class="birthday.contactable ? 'text-gray-400' : 'text-amber-700'"
                >
                  <Smartphone class="size-3 shrink-0" />
                  {{ phoneLabel(birthday) }}
                </span>
                <span class="flex items-center gap-1 text-gray-400">
                  <Gift class="size-3 shrink-0" />
                  <template v-if="birthday.gift">{{ birthday.gift.name }}</template>
                  <template v-else>Aucun cadeau ne le cible</template>
                </span>
              </div>
            </div>

            <GiftStatusBadge
              v-if="birthday.voucher"
              class="flex-none"
              :status="birthday.voucher.status"
              :blocked-reason="birthday.voucher.blocked_reason"
              :sent-at="birthday.voucher.sent_at"
              :send-hour="sendHour"
            />

            <Button
              class="flex-none"
              :class="isAutomatic(birthday) ? '' : 'bg-primary'"
              :variant="isAutomatic(birthday) ? 'outline' : 'default'"
              size="sm"
              :disabled="refusalOf(birthday) !== null || isSending(birthday)"
              :title="refusalOf(birthday)
                ?? (sendsGift(birthday)
                  ? 'Envoyer son cadeau d\'anniversaire'
                  : 'Envoyer un simple message d\'anniversaire, sans cadeau')"
              @click="send(birthday)"
            >
              <Send class="size-3.5" />
              {{ isSending(birthday) ? 'Envoi…' : (isAutomatic(birthday) ? 'Envoyer maintenant' : 'Envoyer') }}
            </Button>
          </div>

          <!-- A number that exists but cannot be texted: say what it means
               and offer the way out — a mobile typed at the counter, used for
               this send alone. -->
          <div v-if="!birthday.contactable" class="flex flex-col gap-2">
            <p
              class="flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1 text-[10.5px] font-medium text-amber-900"
            >
              <TriangleAlert class="mt-px size-3 shrink-0" />
              <!-- One span, two sentences: the parent is a flex row, and
                   separate spans would sit side by side instead of flowing. -->
              <span>
                <template v-if="birthday.phone">
                  Ce numéro ne permet pas l'envoi du message : ce n'est pas un mobile, ni RCS
                  ni SMS ne peuvent l'atteindre.
                </template>
                <template v-else>
                  Aucun numéro au dossier ne permet l'envoi du message.
                </template>
                <!-- Dropped once a mobile is typed: the line below then says
                     what will happen, and repeating the instruction reads as
                     a warning that nothing was done. -->
                <template v-if="typedPhoneOf(birthday) === null">
                  Saisissez son mobile pour lui écrire : il est enregistré sur sa fiche,
                  l'opération n'est à faire qu'une fois.
                </template>
              </span>
            </p>

            <div class="flex items-center gap-2">
              <input
                :value="typedPhones[birthday.customer_id] ?? ''"
                type="tel"
                inputmode="numeric"
                placeholder="06 12 34 56 78"
                :disabled="!isToday"
                class="h-8 w-40 rounded-lg border border-slate-200 bg-transparent px-3 text-xs shadow-sm
                  placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-1
                  focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                @input="onPhoneInput(birthday, $event)"
              >
              <!-- The number is checked as it is typed: the send button is
                   the only other signal, and it says nothing about why. -->
              <span
                v-if="typedPhones[birthday.customer_id] && typedPhoneOf(birthday) === null"
                class="text-[10.5px] font-medium text-amber-700"
              >
                Mobile incomplet — 06 ou 07 à dix chiffres.
              </span>
              <span
                v-else-if="typedPhoneOf(birthday)"
                class="text-[10.5px] font-medium text-primary"
              >
                Le message partira sur ce numéro, enregistré pour ce patient.
              </span>
            </div>
          </div>

          <!-- The dispatch will send it on its own: said plainly, so nobody
               sends a second message by hand. -->
          <p
            v-else-if="isAutomatic(birthday)"
            class="flex items-center gap-1.5 rounded-md bg-primary-lightest px-2 py-1 text-[10.5px] font-medium text-primary"
          >
            <Send class="size-3" />
            Envoi automatique prévu à {{ String(sendHour ?? 9).padStart(2, '0') }}:00 — inutile d'agir.
          </p>

          <!-- No gift targets this patient: the counter picks one, for a
               patient reachable from the file or from the number just typed. -->
          <div
            v-if="!birthday.gift && !isAutomatic(birthday)"
            class="flex flex-wrap items-center gap-2"
          >
            <Select
              v-if="gifts.length"
              :model-value="pickedGifts[birthday.customer_id] ? String(pickedGifts[birthday.customer_id]) : undefined"
              :disabled="!isToday"
              @update:model-value="value => pickedGifts[birthday.customer_id] = Number(value)"
            >
              <SelectTrigger class="h-8 flex-1 text-xs">
                <span v-if="pickedGifts[birthday.customer_id]">
                  {{ gifts.find(gift => gift.id === pickedGifts[birthday.customer_id])?.name }}
                </span>
                <SelectValue v-else placeholder="Choisir un cadeau (sinon simple message)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="gift in gifts" :key="gift.id" :value="String(gift.id)">
                  {{ gift.name }}
                </SelectItem>
              </SelectContent>
            </Select>
            <p v-else class="flex-1 text-[10.5px] text-gray-400">
              Aucun cadeau actif dans la pharmacie.
            </p>
            <Button variant="outline" size="sm" class="flex-none" @click="emits('createGift')">
              <Plus class="size-3.5" />
              Créer un cadeau
            </Button>
          </div>
        </div>
      </div>

      <div
        v-if="birthdays.length && isToday"
        class="mt-1 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-3"
      >
        <p class="flex-1 text-[10.5px] text-gray-400">
          Chaque envoi consomme un crédit SMS.
          <template v-if="wishesOnlyCount">
            {{ wishesOnlyCount }}
            {{ wishesOnlyCount > 1 ? 'patients recevront' : 'patient recevra' }}
            un simple message d'anniversaire, sans cadeau.
          </template>
          <template v-if="unreachableCount">
            {{ unreachableCount }}
            {{ unreachableCount > 1 ? 'patients sont écartés' : 'patient est écarté' }} :
            aucun numéro ne permet l'envoi.
          </template>
        </p>
        <Button
          class="bg-primary"
          :disabled="!recipients.length || sendingAll"
          @click="emits('sendAll', recipients)"
        >
          <Send class="size-4" />
          {{ sendingAll ? 'Envoi…' : `Envoyer à tous (${recipients.length})` }}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
